import api, { getCsrfHeaders } from './api';

export interface RegionColorEntry {
  region_code: string;
  region_name: string;
}

export interface UserPreferencesResponse {
  region_colors: Record<string, string>;
  region_color_defaults: Record<string, string>;
  region_color_overrides: Record<string, string>;
  region_color_templates: RegionColorTemplate[];
  active_region_color_template_id: string | null;
  regions: RegionColorEntry[];
}

export interface RegionColorTemplate {
  id: string;
  name: string;
  colors: Record<string, string>;
}

export interface ConnectionLabel {
  id?: number;
  name: string;
  display_order: number;
  /** Hex colour ("#RRGGBB"); '' means fall back to the palette default. */
  color: string;
}

export interface ConnectionLabelRule {
  id?: number;
  label_id?: number;
  label_name: string;
  priority: number;
  min_duration_minutes: number | null;
  max_duration_minutes: number | null;
  origin_region: string;
  destination_region: string;
  via_hub: string;
}

export interface ConnectionLabelSet {
  id: number;
  name: string;
  description: string;
  is_active: boolean;
  archived_at?: string | null;
  created_at: string | null;
  updated_at: string | null;
  labels: ConnectionLabel[];
  rules: ConnectionLabelRule[];
}

export interface ConnectionLabelSetPayload {
  name: string;
  description: string;
  is_active: boolean;
  labels: ConnectionLabel[];
  rules: ConnectionLabelRule[];
}

export interface ConnectionLabelSetsResponse {
  label_sets: ConnectionLabelSet[];
  active_label_set_id: number | null;
  regions: RegionColorEntry[];
}

export const userPreferencesApi = {
  get: async (): Promise<UserPreferencesResponse> => {
    const response = await api.get('/user-preferences/');
    return response.data;
  },

  saveRegionColors: async (
    overrides: Record<string, string>,
    activeRegionColorTemplateId = '',
  ): Promise<UserPreferencesResponse> => {
    const csrfHeaders = await getCsrfHeaders();
    const response = await api.put(
      '/user-preferences/',
      {
        region_color_overrides: overrides,
        active_region_color_template_id: activeRegionColorTemplateId,
      },
      { headers: csrfHeaders }
    );
    return response.data;
  },

  saveRegionColorTemplates: async (
    templates: RegionColorTemplate[]
  ): Promise<RegionColorTemplate[]> => {
    const csrfHeaders = await getCsrfHeaders();
    const response = await api.put(
      '/user-preferences/region-color-templates/',
      { templates },
      { headers: csrfHeaders }
    );
    return Array.isArray(response.data?.templates) ? response.data.templates : [];
  },
};

export const connectionLabelSetsApi = {
  list: async (): Promise<ConnectionLabelSetsResponse> => {
    const response = await api.get('/connection-label-sets/');
    return response.data;
  },

  create: async (payload: ConnectionLabelSetPayload): Promise<ConnectionLabelSet> => {
    const csrfHeaders = await getCsrfHeaders();
    const response = await api.post('/connection-label-sets/', payload, { headers: csrfHeaders });
    return response.data;
  },

  update: async (
    id: number,
    payload: ConnectionLabelSetPayload,
  ): Promise<ConnectionLabelSet> => {
    const csrfHeaders = await getCsrfHeaders();
    const response = await api.put(`/connection-label-sets/${id}/`, payload, {
      headers: csrfHeaders,
    });
    return response.data;
  },

  remove: async (id: number): Promise<void> => {
    const csrfHeaders = await getCsrfHeaders();
    await api.delete(`/connection-label-sets/${id}/`, { headers: csrfHeaders });
  },

  // Archived (soft-deleted) schemes, restorable via restore()
  listArchived: async (): Promise<ConnectionLabelSet[]> => {
    const response = await api.get('/connection-label-sets/', {
      params: { archived: 'true' },
    });
    return response.data.label_sets;
  },

  restore: async (id: number): Promise<ConnectionLabelSet> => {
    const csrfHeaders = await getCsrfHeaders();
    const response = await api.post(`/connection-label-sets/${id}/restore/`, undefined, {
      headers: csrfHeaders,
    });
    return response.data;
  },
};
